// import { apiClient } from "@/lib/apiClient"; // axios com interceptors configurado

import apiClient, { apiClientIdToken } from "@/lib/api";

// Tipos baseados no contrato da API
export interface Client {
  clientId: number;
  clientFantasyName: string;
  clientCodeCnx: string;
  clientCity: string;
  clientState: string;
  isInadimplente?: boolean;
}

export interface ClientProduct {
  profileClientId: number;
  productId: number;
  productCode: string;
  productDescription: string;
  productSchoolYear: string;
  contractualQuantity: number;
  discount: number;
  adjustment: number;
  productPrice: number;
  priceWithoutDiscount?: number;
  priceWithDiscount?: number;
  totalWithoutDiscount?: number;
  totalWithDiscount: number;
  segmentName: string;
  seriesName: string;
  materialUseName: string;
  brandName?: string;
  productTypeName?: string;
  deliveryTypeName?: string;
  content?: string;
  isActive?: boolean;
}

export interface ClientAdjustment {
  id?: number;
  clientId: number;
  year: number;
  cycleYear?: number;
  adjustment: number;
  comment?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SaveClientAdjustmentDto {
  year?: number;
  cycleYear?: number;
  adjustment: number;
  comment?: string;
}

export const clientService = {
  /**
   *  Buscar todos os clientes da base
   * GET /clients/profile-clients
   */
  async getAllClients(): Promise<Client[]> {
    try {
      const { data } = await apiClient.get<{ data: Client[]}>("/clients/profile-clients");
      return data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar todos os clientes:", error);
      return [];
    }
  },

  /**
   * Buscar clientes pelo termo
   * GET /clients/filter?SearchTerm={termo}
   */
  async getClients(searchTerm: string): Promise<Client[]> {
    try {
      const { data } = await apiClient.get<{ data: Client[] | null}>(
        `/clients/filter?SearchTerm=${encodeURIComponent(searchTerm)}`
      );
      return data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar clientes filtrados:", error);
      return [];
    }
  },

  /**
   *  Buscar detalhes do cliente (produtos)
   * GET /clients/{client_id}/profile-details
   */
  async getClientProducts(clientId: number, cycleYear?: number): Promise<ClientProduct[]> {
    const { data } = await apiClient.get<{ data: ClientProduct[] | null }>(
      `/clients/${clientId}/profile-details${cycleYear ? `?cycleYear=${cycleYear}` : ""}`
    );

    if (Array.isArray(data)) {
      // O backend retorna agrupado por materialUseName, precisamos achatar:
      return data.flatMap((group: any) =>
        group.products.map((p: any) => ({
          ...p,
          materialUseName: group.materialUseName,
        }))
      );
    }

    return [];
  },

  /**
   * Atualizar produto do cliente
   * PUT /profile-client/{profile_client_id}
   */
  async updateClientProduct(
    profileClientId: number,
    payload: {
      contractualQuantity: number;
      discount: number;
      adjustment: number;
      content: string;
      productId: number;
      productPrice: number;
      // Produto opcional (true) ou obrigatório (false); ausente mantém o valor atual
      isOptional?: boolean;
      // Sem frete e sem remessa impressa (true) ou não (false); ausente mantém o valor atual
      isShipmentExempt?: boolean;
    }
  ) {
    try {
      const { data } = await apiClientIdToken.put<any>(
        `/profile-client/${profileClientId}`,
        payload
      );
      console.log("✅ Produto atualizado:", data);
      return data;
    } catch (error) {
      console.error("❌ Erro na atualização do produto:", error);
      throw error;
    }
  },

  /**
   * Buscar histórico de reajustes do cliente
   * GET /clients/{clientId}/adjustments
   */
  async getClientAdjustments(clientId: number): Promise<ClientAdjustment[]> {
    try {
      const { data } = await apiClient.get<{ data: ClientAdjustment[] } | ClientAdjustment[]>(
        `/clients/${clientId}/adjustments`
      );
      const rawList = Array.isArray(data) ? data : (data && 'data' in data && Array.isArray(data.data)) ? data.data : [];
      return rawList.map((item: any) => ({
        ...item,
        year: item.year ?? item.cycleYear ?? 0,
        cycleYear: item.cycleYear ?? item.year ?? 0,
      }));
    } catch (error) {
      console.error("❌ Erro ao buscar histórico de reajustes do cliente:", error);
      return [];
    }
  },

  /**
   * Buscar reajuste de um ciclo específico
   * GET /clients/{clientId}/adjustments/{year}
   */
  async getClientAdjustmentByYear(clientId: number, year: number): Promise<ClientAdjustment | null> {
    try {
      const { data } = await apiClient.get<{ data: ClientAdjustment } | ClientAdjustment>(
        `/clients/${clientId}/adjustments/${year}`
      );
      const raw = (data && 'data' in data) ? (data as any).data : data;
      if (!raw) return null;
      return {
        ...raw,
        year: raw.year ?? raw.cycleYear ?? year,
        cycleYear: raw.cycleYear ?? raw.year ?? year,
      };
    } catch (error) {
      console.error(`❌ Erro ao buscar reajuste do ciclo ${year}:`, error);
      return null;
    }
  },

  /**
   * Criar/Atualizar ciclo de reajuste do cliente
   * POST /clients/{clientId}/adjustments
   */
  async saveClientAdjustment(clientId: number, payload: SaveClientAdjustmentDto): Promise<ClientAdjustment> {
    try {
      const yearValue = payload.cycleYear ?? payload.year ?? new Date().getFullYear();
      const body = {
        cycleYear: yearValue,
        year: yearValue,
        adjustment: payload.adjustment,
        comment: payload.comment,
      };
      const { data } = await apiClientIdToken.post<any>(
        `/clients/${clientId}/adjustments`,
        body
      );
      console.log("✅ Reajuste do cliente salvo:", data);
      const res = data?.data || data;
      return {
        ...res,
        year: res?.year ?? res?.cycleYear ?? yearValue,
        cycleYear: res?.cycleYear ?? res?.year ?? yearValue,
      };
    } catch (error) {
      console.error("❌ Erro ao salvar reajuste do cliente:", error);
      throw error;
    }
  },

  /**
   * Remover ciclo de reajuste do cliente
   * DELETE /clients/{clientId}/adjustments/{year}
   */
  async deleteClientAdjustment(clientId: number, year: number): Promise<void> {
    try {
      await apiClientIdToken.delete(`/clients/${clientId}/adjustments/${year}`);
      console.log(`✅ Reajuste do ciclo ${year} removido com sucesso.`);
    } catch (error) {
      console.error(`❌ Erro ao remover reajuste do ciclo ${year}:`, error);
      throw error;
    }
  },

  /**
   * Carga em lote de reajustes
   * POST /clients/adjustments/bulk
   */
  async bulkUpdateClientAdjustments(payload: { clientId: number; year?: number; cycleYear?: number; adjustment: number }[]): Promise<any> {
    try {
      const formatted = payload.map(p => {
        const y = p.cycleYear ?? p.year ?? new Date().getFullYear();
        return {
          ...p,
          cycleYear: y,
          year: y,
        };
      });
      const { data } = await apiClientIdToken.post<any>("/clients/adjustments/bulk", formatted);
      return data;
    } catch (error) {
      console.error("❌ Erro ao realizar carga em lote de reajustes:", error);
      throw error;
    }
  },
};

